import { useTranslation } from "react-i18next"
import { useLazyGetUsersByProjectsQuery } from "../../../redux/api/content"
import { useAppSelector } from "../../../hooks"
import { selectUser } from "../../../redux/slice/auth"
import { useEffect, useMemo, useRef, useState } from "react"
import { IEmployee } from "../../../types/content"
import Employee from "../Employee/Employee"
import clsx from "clsx"
import TitleBack from "../../TitleBack/TitleBack"
import SearchSelect from "../../SearchSelect/SearchSelect"
import ProfileBlock from "../../ProfileBlock/ProfileBlock"
import { v4 as uuidv4 } from "uuid"
import styles from "../Employees.module.scss"

const LIMIT_USERS = 20

const EmployeesByProjects = () => {
  const { t } = useTranslation("translation", { keyPrefix: `interface` })

  const user = useAppSelector(selectUser)

  const [getUsersByProject, { isFetching, isLoading, isUninitialized }] = useLazyGetUsersByProjectsQuery()

  const [items, setItems] = useState<{ project_id: string; project_name: string; users: IEmployee[] }[]>([])
  const [offset, setOffset] = useState<number>(0)
  const loaderRef = useRef<HTMLDivElement>(null)
  const [isSearchLoading, setSearchLoading] = useState<boolean>(false)
  const [isAllowFetched, setAllowFetched] = useState<boolean>(false)
  const [isListEnd, setListEnd] = useState<boolean>(false)
  const [searchedUser, setSearchedUser] = useState<{
    project_id: string
    project_name: string
    user: IEmployee
  } | null>(null)

  useEffect(() => {
    if (!user?.id) return
    getUsersByProject({ limit: LIMIT_USERS })
      .unwrap()
      .then((res) => {
        if (res?.data) {
          setItems(() => res.data || [])
          setListEnd(res.bIsEnd)
          setAllowFetched(true)
        }
      })
      .catch((err) => console.error("error in getUsersByProject:", err))
  }, [])

  const fetchData = async (isStart?: boolean) => {
    if (isListEnd || isFetching || !isAllowFetched || searchedUser) return
    setOffset((prev) => (isStart ? 0 : prev + LIMIT_USERS))
    setAllowFetched(false)
    await getUsersByProject({
      limit: LIMIT_USERS,
      offset: isStart ? 0 : offset + LIMIT_USERS,
    })
      .unwrap()
      .then((res) => {
        setItems((prevItems) => {
          const itemsMap = new Map(prevItems.map((item) => [item.project_id, item]))
          res.data.forEach((p) => {
            if (itemsMap.has(p.project_id)) {
              const existing = itemsMap.get(p.project_id)
              if (existing) {
                itemsMap.set(p.project_id, {
                  ...existing,
                  users: [...existing.users, ...p.users],
                })
              }
            } else {
              itemsMap.set(p.project_id, p)
            }
          })
          return Array.from(itemsMap.values())
        })
        setListEnd(res.bIsEnd)
      })
      .catch((err) => console.error("error in getUsersByProject:", err))
    setSearchLoading(false)
    setTimeout(() => {
      setAllowFetched(true)
    }, 0)
  }

  const list = useMemo(() => {
    if (!items?.length) return null
    return items.map((proj) => {
      return (
        <div key={proj.project_id} className={styles.project}>
          <h2 className={styles.projectTitle}>{proj.project_name}</h2>
          <div className={styles.grid}>
            {proj.users.map((employee) => {
              const idE = uuidv4()
              return (
                <Employee
                  key={employee.id + idE}
                  {...employee}
                  projectID={proj.project_id}
                  // highlightTxt={searchChat}
                  className={clsx(styles.item)}
                />
              )
            })}
          </div>
        </div>
      )
    })
  }, [items, user?.id])

  useEffect(() => {
    if (isUninitialized) return
    const observer = new IntersectionObserver((entries) => {
      const target = entries[0]
      if (target.isIntersecting) {
        if (isFetching || isSearchLoading || !isAllowFetched) return
        void fetchData(false)
      }
    })

    if (loaderRef.current) {
      observer.observe(loaderRef.current)
    }

    return () => {
      if (loaderRef.current) {
        observer.unobserve(loaderRef.current)
        observer.disconnect()
      }
    }
  }, [isUninitialized, isFetching, isLoading, isSearchLoading, isAllowFetched, list, searchedUser])

  return (
    <>
      <TitleBack
        noLinkBack
        title={t("employees")}
        onClick={
          searchedUser
            ? () => {
                setSearchedUser(null)
              }
            : undefined
        }
      />

      {!searchedUser && (
        <SearchSelect setSelectedVal={setSearchedUser} selectedVal={searchedUser} className={styles["search-select"]} />
      )}

      {!isUninitialized && !isSearchLoading && (
        <>
          {searchedUser ? (
            <ProfileBlock
              profileData={searchedUser?.user}
              projectID={searchedUser?.project_id}
              notViewPhones
              isWriteMes={user.id !== searchedUser?.user?.id}
              className={styles["employee-profile-block"]}
            />
          ) : (
            <>
              {list && list?.length > 0 ? (
                <div className={clsx("employees__grid")}>{list}</div>
              ) : (
                <>
                  {!isSearchLoading && !isFetching && !isLoading && (
                    <div className={styles.nothing}>{t("noEmployeesYet")}</div>
                  )}
                </>
              )}
            </>
          )}
        </>
      )}
      <div ref={loaderRef} className={clsx("employees__grid", styles.grid)}>
        {(isSearchLoading || isFetching) &&
          [...Array(4)].map((_item, index) => <div key={index} className={clsx(styles.skeleton, "skeletonBlock")} />)}
      </div>
    </>
  )
}

export default EmployeesByProjects
